import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router";
import { styled } from "styled-components";
import { useDebouncedCallback } from "use-debounce";

import { knowledgeApiSlice } from "api/knowledgeApi.slice";
import { FilterButton, MasterDetailLayout } from "common/components";
import { UIInput } from "common/components/form/UIInput";
import { useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { getIsAuthenticated } from "domains/authentication/selectors";
import { activateModal } from "domains/modal/actions";
import { PublicationDetailSection } from "domains/publication/components/PublicationDetailSection";
import { PublicationList } from "domains/publication/components/PublicationList";
import { searchSlice } from "domains/search/search.slice";

const StyledInput = styled(UIInput)`
  margin-bottom: 1rem;
`;

const StyledSearchInputContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

export const SearchKnowledgePage = () => {
  const { knowledgeId } = useParams();
  const dispatch = useDispatch();
  const track = useTrack();
  const translate = useTranslate();

  const isAuthenticated = useSelector(getIsAuthenticated);
  const currentSearchQuery = useSelector((state) => state.search.currentSearchQuery);
  const selectedSources = useSelector((state) => state.search.sources);

  const [searchInput, setSearchInput] = useState(currentSearchQuery);

  const { updateSearchQuery } = searchSlice.actions;

  const debouncedUpdateSearchQuery = useDebouncedCallback((searchQuery: string) => {
    dispatch(updateSearchQuery({ query: searchQuery }));
  }, 1000);

  useEffect(() => {
    debouncedUpdateSearchQuery(searchInput);
  }, [currentSearchQuery, debouncedUpdateSearchQuery, dispatch, searchInput, updateSearchQuery]);

  const {
    data: paginatedPublicationsSearchResults,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = knowledgeApiSlice.endpoints.searchPublications.useInfiniteQuery({
    searchQuery: currentSearchQuery,
    sourceNames: selectedSources.map((source) => source.name),
  });

  const publicationsSearchResults = paginatedPublicationsSearchResults?.pages.flat() ?? [];

  const publication = knowledgeId
    ? publicationsSearchResults.find((publication) => publication.id === knowledgeId)
    : undefined;

  useEffect(() => {
    if (publication)
      track({
        entityId: publication.id,
        entityName: publication.title,
        entityType: "Publication",
        name: "Browse page",
        page: "Search",
        subpage: "Knowledge detail",
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publication?.id, track]);

  const loadNextPage = useCallback(async () => {
    if (hasNextPage && !isFetching) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetching, fetchNextPage]);

  const getLink = (knowledgeId: string) => `/search/publications/${knowledgeId}`;

  const knowledgeList = (
    <PublicationList
      publications={publicationsSearchResults.length === 0 && publication ? [publication] : publicationsSearchResults}
      selectedId={publication?.id}
      trackPage="Search"
      showEmptyMessage={!!currentSearchQuery}
      emptyMessage={translate("app.search.results.no_results")}
      isPending={isFetching}
      hasNextPage={hasNextPage}
      getToLink={getLink}
      loadNextPage={loadNextPage}
      lockTiles={!isAuthenticated}
    />
  );

  const shareLink = `${import.meta.env.VITE_WEBSITE_URL}${getLink(publication?.id ?? "")}`;

  const getFilterCount = () => {
    let count = 0;

    count += selectedSources.length;

    return count;
  };

  const openFilterModal = () => dispatch(activateModal({ id: "KnowledgeSearchFilter" }));

  return (
    <MasterDetailLayout
      headerElement={
        <StyledSearchInputContainer>
          <StyledInput
            clear={() => setSearchInput("")}
            name="publicationSearch"
            onChange={setSearchInput}
            placeholder={translate("app.discover.topic.tabs.knowledge.filter.search_title")}
            value={searchInput}
          />
          <FilterButton count={getFilterCount()} onClick={openFilterModal} />
        </StyledSearchInputContainer>
      }
      listTitle={translate("app.discover.topic.tabs.knowledge.title")}
      listComponent={knowledgeList}
      listBackNavigation={{
        label: translate("app.search.title"),
        path: "/search",
      }}
      detailBackNavigation={{
        label: translate("app.discover.topic.tabs.knowledge.title"),
        path: "/search/publications",
      }}
      mainView={knowledgeId ? "right" : "left"}
    >
      <PublicationDetailSection
        id={knowledgeId}
        shareLink={shareLink}
        topicBack={{ term: "app.discover.topic.tabs.knowledge.title" }}
      />
    </MasterDetailLayout>
  );
};
